import React, { useEffect, useState } from 'react';
import './App.css';

const App = () => {
  const [scrollY, setScrollY] = useState(0);
  const [navBackground, setNavBackground] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.pageYOffset);
      setNavBackground(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Scroll reveal animation
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.scroll-reveal').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const heroOpacity = Math.max(1 - scrollY / window.innerHeight, 0);
  const heroTransform = `translateY(${scrollY * -0.5}px)`;

  return (
    <div className="bg-gray-50 text-gray-800">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full backdrop-blur-md z-50 border-b border-gray-100 transition-colors ${navBackground ? 'bg-white/90' : 'bg-white/80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="text-xl sm:text-2xl font-bold logo-text lowercase">seeso</div>
            <div className="hidden lg:flex space-x-8">
              <a href="#vision" onClick={(e) => handleSmoothScroll(e, '#vision')} className="text-gray-600 hover:text-gray-900 transition-colors">비전</a>
              <a href="#services" onClick={(e) => handleSmoothScroll(e, '#services')} className="text-gray-600 hover:text-gray-900 transition-colors">서비스</a>
              <a href="#journey" onClick={(e) => handleSmoothScroll(e, '#journey')} className="text-gray-600 hover:text-gray-900 transition-colors">여정</a>
              <a href="#contact" onClick={(e) => handleSmoothScroll(e, '#contact')} className="text-gray-600 hover:text-gray-900 transition-colors">함께하기</a>
            </div>
            <div className="flex space-x-2 sm:space-x-4">
              <a href="https://second-team.com/" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block btn-secondary text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3">세컨팀</a>
              <a href="https://careerly.co.kr/" target="_blank" rel="noopener noreferrer" className="btn-primary text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-3">커리어리</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        id="hero" 
        className="hero-fade min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20"
        style={{ opacity: heroOpacity, transform: heroTransform }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="floating-animation mb-6 sm:mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto lemon-gradient rounded-2xl flex items-center justify-center mb-6 sm:mb-8 pulse-animation">
              <i className="fas fa-rocket text-black text-xl sm:text-2xl"></i>
            </div>
          </div>
          
          <h1 className="text-3xl sm:text-5xl md:text-7xl mb-6 sm:mb-8 leading-relaxed">
            <span className="text-gray-600 font-normal text-2xl sm:text-4xl md:text-5xl block mb-2">기술과 사람 사이,</span>
            <span className="text-black font-bold text-3xl sm:text-5xl md:text-7xl block mb-2">우리는 미래 세대의 <span className="inline-block" style={{textDecoration: 'underline', textDecorationColor: 'rgb(229, 255, 30)', textDecorationThickness: '3px'}}>'일'</span>을</span>
            <span className="text-gray-900 font-bold text-3xl sm:text-5xl md:text-7xl block">다시 설계합니다</span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-8 sm:mb-12 max-w-3xl mx-auto font-normal px-4">
            AI 시대의 커리어 인프라를 만드는 팀, <span className="text-black font-bold">seeso</span>
          </p>
          
          <div className="flex justify-center">
            <a href="#vision" onClick={(e) => handleSmoothScroll(e, '#vision')} className="btn-primary text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4">우리의 비전 보기</a>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="section-spacing bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">우리가 만드는 미래</span>
            </h2>
            <div className="w-20 h-1 lemon-bg mx-auto mb-12"></div>
          </div>
          
          <div className="scroll-reveal max-w-4xl mx-auto text-center">
            <p className="text-2xl md:text-3xl text-gray-800 mb-8 font-medium">
              우리는 하나의 질문에서 출발했습니다.
            </p>
            <p className="text-3xl md:text-4xl text-black font-bold mb-12">
              '일'은 앞으로 어떻게 변할 것인가?
            </p>
            
            <div className="bg-gray-50 rounded-3xl p-12 mb-16">
              <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium">
                시소는 미래 세대의 <strong className="text-black">일과 노동의 본질</strong>에 대해 고민하는 팀입니다.<br/>
                우리는 외주, 협업, 채용, 교육, 성장 전반에 걸쳐<br/>
                AI와 데이터를 기반으로 <strong className="text-black">커리어의 새로운 인프라</strong>를 만들고 있습니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-spacing bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">두 개의 축</span> - seeso의 실험
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* 세컨팀 */}
            <div className="scroll-reveal card-hover bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg">
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-8 lemon-border border-2">
                <i className="fas fa-globe lemon-text text-2xl"></i>
              </div>
              
              <h3 className="text-2xl md:text-3xl font-medium mb-6">
                <span className="text-black font-bold">세컨팀</span> - 아웃소싱의 미래
              </h3>
              
              <div className="space-y-6 text-gray-700">
                <p className="text-lg font-medium">
                  과거 우리는 사람을 연결해 일했습니다.<br/>
                  이제는 <strong className="font-bold">AI 도구와 사람을 연결해 일합니다.</strong>
                </p>
                
                <p className="font-medium">
                  세컨팀은 GPT, Claude, Jules 등의 도구를 활용해<br/>
                  기획/디자인/개발의 경계를 허문 새로운 아웃소싱을 실험합니다.<br/>
                  누구나 바이브코딩을 통해<br/>
                  <strong className="font-bold">10배 빠르게 실험하고, 실패하고, 성공할 수 있는 구조</strong>를 제공합니다.
                </p>
                
                <div className="lemon-bg-soft p-6 rounded-2xl border lemon-border">
                  <p className="text-gray-800 font-semibold">
                    → 우리는 더 이상 '외주'라고 부르지 않습니다.<br/>
                    이것은 <strong className="text-xl font-bold" style={{textDecoration: 'underline', textDecorationColor: 'rgb(229, 255, 30)', textDecorationThickness: '3px'}}>새로운 일의 방식</strong>입니다.
                  </p>
                </div>
              </div>
              
              <a href="https://second-team.com/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-block mt-8">세컨팀 더 알아보기</a>
            </div>
            
            {/* 커리어리 */}
            <div className="scroll-reveal card-hover bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg">
              <div className="w-16 h-16 lemon-gradient rounded-2xl flex items-center justify-center mb-8">
                <i className="fas fa-robot text-black text-2xl"></i>
              </div>
              
              <h3 className="text-2xl md:text-3xl font-medium mb-6">
                <span className="text-black font-bold">커리어리</span> - 커리어의 OS + AI 레이어
              </h3>
              
              <div className="space-y-6 text-gray-700">
                <p className="text-lg font-medium">
                  커리어리는 단순한 이직 플랫폼이 아닙니다.<br/>
                  우리는 "이력서 이후의 커리어"를 설계합니다.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-black rounded-full"></div>
                    <p className="font-medium">내가 해온 모든 '일의 흔적'을 데이터로 만들고</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
                    <p className="font-medium">AI가 나보다 나를 더 잘 아는 커리어 코치가 되어</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                    <p className="font-medium">나에게 필요한 연결, 추천, 전략을 제안합니다</p>
                  </div>
                </div>
                
                <div className="bg-gray-900 p-6 rounded-2xl">
                  <p className="text-white font-semibold">
                    이제 커리어는 정리된 요약이 아니라,<br/>
                    <strong className="lemon-text">살아 있는 로그와 가능성의 지도</strong>가 됩니다.
                  </p>
                </div>
              </div>
              
              <a href="https://careerly.co.kr/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-block mt-8">커리어리 바로가기</a>
            </div>
          </div>
        </div>
      </section>

      {/* Connection Vision */}
      <section className="section-spacing bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-light mb-16 text-center">
              <span className="text-black font-bold">우리가 꿈꾸는 미래</span>
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="space-y-6 slide-in-left order-2 md:order-1">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  두 가지 혁신이 만드는 하나의 세상
                </h3>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  세컨팀은 AI로 일하는 방법을 변화시키고,<br/>
                  커리어리는 AI로 성장하는 방법을 혁신합니다.
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  이 두 서비스는 결국 하나의 비전으로 통합됩니다.<br/>
                  <strong className="text-black text-lg sm:text-xl block mt-4">모든 사람이 AI와 함께 자신만의 커리어를 창조하는 세상.</strong>
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-200 slide-in-right order-1 md:order-2">
                <h4 className="text-xl font-bold mb-8 text-black">우리의 약속</h4>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-black rounded-full flex-shrink-0 flex items-center justify-center icon-hover">
                      <i className="fas fa-lightbulb text-white text-sm"></i>
                    </div>
                    <div>
                      <h5 className="font-bold mb-2 text-gray-900">모든 일이 경험이 되는 세상</h5>
                      <p className="text-gray-600">프로젝트, 협업, 학습 - 모든 과정이 당신의 성장 자산이 됩니다.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-black rounded-full flex-shrink-0 flex items-center justify-center icon-hover">
                      <i className="fas fa-robot text-white text-sm"></i>
                    </div>
                    <div>
                      <h5 className="font-bold mb-2 text-gray-900">AI가 당신의 커리어 파트너</h5>
                      <p className="text-gray-600">당신보다 당신을 더 잘 이해하는 AI가 최적의 기회를 제안합니다.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-black rounded-full flex-shrink-0 flex items-center justify-center icon-hover">
                      <i className="fas fa-trophy text-white text-sm"></i>
                    </div>
                    <div>
                      <h5 className="font-bold mb-2 text-gray-900">일과 성장의 새로운 표준</h5>
                      <p className="text-gray-600">우리는 커리어 인프라를 통해 일의 미래를 재정의합니다.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section id="journey" className="section-spacing bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">걸어온 길</span> - 우리의 기록
            </h2>
            <p className="text-xl text-gray-700 font-medium max-w-3xl mx-auto">
              시소는 단순한 스타트업이 아닙니다.<br/>
              우리는 수년간 IT 협업, 외주, 커뮤니티, 플랫폼, 데이터, 커리어를 연결해 왔습니다.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {/* 2016-2020 */}
              <div className="scroll-reveal timeline-item">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <span className="lemon-text font-bold text-lg">2016 ~ 2020</span>
                    <span className="ml-4 text-2xl">🚀</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">첫 발걸음</h3>
                  <p className="text-gray-800 font-medium mb-4">
                    "좋은 아이디어가 있지만 IT 개발이 어려운 스타트업을 위해"라는 모토로 시작했습니다.
                    1,000개가 넘는 프로젝트를 성공적으로 수행하며, 신뢰할 수 있는 IT 협업의 새로운 기준을 만들었습니다.
                    원격 근무와 부업 가능한 인재풀 모델은 현재 IT 업계의 표준이 되었습니다.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">법인 설립</span>
                    <span className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">TIPS 선정</span>
                    <span className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">1,000개 스프린트</span>
                  </div>
                </div>
              </div>
              
              {/* 2021-2023 */}
              <div className="scroll-reveal timeline-item">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <span className="text-black font-bold text-lg">2021 ~ 2023</span>
                    <span className="ml-4 text-2xl">🧩</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">플랫폼 전환의 실험</h3>
                  <p className="text-gray-800 font-medium mb-4">
                    "단순한 외주가 아닌, 일하는 방식의 혁신"을 꿈꾸며 세컨팀을 론칭했습니다.
                    프로젝트 매칭 플랫폼을 통해 기업과 인재를 연결하고,
                    AI 시대를 준비하며 일의 데이터화와 자동화를 실험하기 시작했습니다.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm font-medium">세컨팀 론칭</span>
                    <span className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm font-medium">제주창조경제혁신센터 최우수상</span>
                    <span className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm font-medium">AI 서비스 실험</span>
                  </div>
                </div>
              </div>
              
              {/* 2024~ */}
              <div className="scroll-reveal timeline-item">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <span className="lemon-text font-bold text-lg">2024 ~ 2025</span>
                    <span className="ml-4 text-2xl">🔮</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">커리어 인프라 기업으로 도약</h3>
                  <p className="text-gray-800 font-medium mb-4">
                    30만 명의 커리어 커뮤니티 '커리어리'를 인수하며 새로운 도약을 시작했습니다.
                    AI 커리어 에이전트를 개발하고, 글로벌 프로그램을 론칭하며,
                    "일과 성장의 인프라"를 구축하는 커리어 인프라 기업으로 진화했습니다.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">커리어리 인수</span>
                    <span className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">AI 커리어 에이전트</span>
                    <span className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">바이브코딩 세컨팀 리뉴얼</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section id="contact" className="section-spacing bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">함께 만들어갈 파트너</span>
            </h2>
            <p className="text-xl text-gray-700 font-medium">시소와 함께 AI 시대의 새로운 일의 문화를 만들어갈 파트너를 찾습니다</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up">
              <div className="w-16 h-16 lemon-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 icon-hover">
                <i className="fas fa-code text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">AI 개발자</h3>
              <p className="text-gray-700 font-medium">AI 도구와 함께 새로운<br/>개발 문화를 만들어갈 분들</p>
            </div>
            
            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up" style={{animationDelay: '0.1s'}}>
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6 icon-hover">
                <i className="fas fa-rocket lemon-text text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">스타트업 & 크리에이터</h3>
              <p className="text-gray-700 font-medium">아이디어를 빠르게 현실로<br/>만들고 싶은 창업가와 창작자</p>
            </div>
            
            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up" style={{animationDelay: '0.2s'}}>
              <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-6 lemon-border border-2 icon-hover">
                <i className="fas fa-briefcase lemon-text text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">파트너십</h3>
              <p className="text-gray-700 font-medium">함께 혁신적인 프로젝트를<br/>만들어갈 기업 파트너</p>
            </div>
            
            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up" style={{animationDelay: '0.3s'}}>
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6 icon-hover">
                <i className="fas fa-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">투자자</h3>
              <p className="text-gray-700 font-medium">커리어 인프라의 미래를<br/>함께 만들어갈 투자 파트너</p>
            </div>
          </div>
          
          <div className="text-center mt-12 sm:mt-16">
            <a href="mailto:partner@seeso.kr" className="btn-primary text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4">partner@seeso.kr로 연락하기</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-3xl font-bold lemon-text mb-6">seeso</div>
            <p className="text-lg text-gray-400 mb-8">
              We are building the operating system for work and growth in the AI era.
            </p>
          </div>
          
          <div className="border-t border-gray-700 pt-8 space-y-3 text-sm">
            <p className="font-medium text-gray-300">(주)시소 (대표이사: 박병규)</p>
            <p>제주특별자치도 제주시 중앙로217 제주벤처마루 3층 & 서울특별시 성동구 성수일로3길 5-12, 2층</p>
            <p>통신판매업신고번호: 2018-서울강남-04590 | 사업자등록번호: 171-88-00464</p>
            <p>개인정보보호관리자: 박병규</p>
            <p className="text-gray-400 mt-6">Copyright © seeso Inc.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;